import { Smartphone, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Text } from '../../base/Text';
import { Button } from '../../base/Button';
import styles from './InstallPrompt.module.css';

interface InstallPromptProps {
  isOpen: boolean;
  isIOS: boolean;
  onInstall: () => void;
  onDismiss: () => void;
}

/**
 * PWA Install Prompt Component
 * - Android/Desktop: Native install prompt via beforeinstallprompt
 * - iOS: Educational UI showing how to add to home screen
 */
export const InstallPrompt = ({
  isOpen,
  isIOS,
  onInstall,
  onDismiss,
}: InstallPromptProps) => {
  const { t } = useTranslation();
  
  if (!isOpen) return null;

  const handleOverlayClick = () => {
    onDismiss();
  };

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // iOS Educational UI
  if (isIOS) {
    return (
      <div className={styles.overlay} onClick={handleOverlayClick}>
        <div className={styles.modal} onClick={handleModalClick}>
          <Smartphone className={styles.icon} size={48} />
          
          <Text variant="title" as="h2" className={styles.title}>
            {t('install.addToHomeScreen')}
          </Text>
          
          <Text variant="body" muted className={styles.description}>
            {t('install.oneTapAccess')}
          </Text>

          <div className={styles.iosInstructions}>
            <div className={styles.step}>
              <span className={styles.stepNumber}>1</span>
              <div className={styles.stepContent}>
                <Text variant="body">
                  {t('install.tapShareButton')}
                  <span className={styles.stepIcon}>
                    <ShareIcon />
                  </span>
                </Text>
              </div>
            </div>

            <div className={styles.step}>
              <span className={styles.stepNumber}>2</span>
              <div className={styles.stepContent}>
                <Text variant="body">
                  <span dangerouslySetInnerHTML={{ __html: t('install.scrollAndTap') }} />
                </Text>
              </div>
            </div>

            <div className={styles.step}>
              <span className={styles.stepNumber}>3</span>
              <div className={styles.stepContent}>
                <Text variant="body">
                  <span dangerouslySetInnerHTML={{ __html: t('install.tapAdd') }} />
                </Text>
              </div>
            </div>
          </div>

          <div className={styles.actions}>
            <Button variant="ghost" onClick={onDismiss} fullWidth>
              {t('install.gotIt')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Android/Desktop UI
  // Detect Samsung Browser or other browsers that might not support prompt
  const isSamsungBrowser = /SamsungBrowser/i.test(navigator.userAgent);
  
  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal} onClick={handleModalClick}>
        <Zap className={styles.icon} size={48} />
        
        <Text variant="title" as="h2" className={styles.title}>
          {isSamsungBrowser ? t('install.addToHomeScreenSamsung') : t('install.saveLinksFaster')}
        </Text>
        
        <Text variant="body" muted className={styles.description}>
          {isSamsungBrowser 
            ? t('install.samsungInstructions')
            : t('install.installForOneTap')
          }
        </Text>

        <div className={styles.actions}>
          {!isSamsungBrowser && (
          <Button variant="primary" onClick={onInstall} fullWidth>
            {t('install.install')}
          </Button>
          )}
          <Button variant="ghost" onClick={onDismiss} fullWidth>
            {isSamsungBrowser ? t('install.gotIt') : t('install.maybeLater')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// iOS Share Icon (SF Symbol style)
const ShareIcon = () => (
  <svg
    className={styles.shareIcon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

